import {
  FRAME_PER_TICK,
  HILL_GRAVITY,
  HILL_OVERSPEED,
  LAT_CENTER,
  LAT_CLAMP,
  LAT_START,
  SPEED_QUANT,
  UNIT_PER_TICK_FACTOR,
  WALL_DIST,
} from "./constants";
import { SURFACE_FX, TrackDef, curvatureAtPos, elevationAtPos, surfaceAtPos } from "./track";

export interface Machine {
  name: string;
  vmax: number; // km/h without jet
  jmax: number; // km/h with jet
  accel: number; // km/h per tick
  jetAccel: number; // km/h per tick while the jet burns
  fuel: number; // litres
}

/** Top speeds retuned in 1.35.0 (balance found by simulation, see CHANGELOG): the ground speed is quantised in steps of 16 km/h, so only the step a top speed falls into matters. Both machines measured on the original (accelerations per 5.7158-frame tick). Machine B's jet accelerates harder than its throttle since 1.30.0 (the original made no difference); its listed stats are unchanged. */
export const MACHINES: Machine[] = [
  { name: "A", vmax: 290, jmax: 360, accel: 11.5, jetAccel: 23.5, fuel: 70 },
  { name: "B", vmax: 315, jmax: 358, accel: 12, jetAccel: 21, fuel: 60 }, // jmax was 350 until physics revision 1 (see PHYS_REV)
];

/**
 * Vehicle categories. Class 0 is the original formula car and keeps every number above; the others change the handling
 * (steer: lateral response, push: how hard curves throw the car out, paceScale: CPU pace relative to the formula cars).
 * Indices are permanent (daily boards and the admin settings refer to them).
 */
export interface VClass {
  name: string;
  machines: Machine[]; // type A, type B
  steer: number;
  push: number;
  paceScale: number;
  /** Visual only: packs the road markings and the roadside closer together so a slow class feels faster (default 1). */
  look?: number;
  /** Share of the speed kept when the car hits the barrier (default WALL_SPEED_FACTOR) and the speed cap after the hit (default WALL_SPEED_CAP): a rally car bounces off with less loss. */
  wall?: number;
  /** Drift: share of the lateral speed kept from one frame to the next (0 = none). The car slides on after a steer or a curve and needs a counter-steer to catch it. */
  drift?: number;
  wallCap?: number;
  tagA: string; // card captions of the two types
  tagB: string;
}
export const VCLASSES: VClass[] = [
  { name: "FORMULA", machines: MACHINES, steer: 1, push: 1, paceScale: 1, tagA: "POWER", tagB: "SPEED" },
  {
    name: "RALLY",
    machines: [
      { name: "A", vmax: 276, jmax: 300, accel: 11, jetAccel: 20, fuel: 60 },
      { name: "B", vmax: 268, jmax: 296, accel: 14.5, jetAccel: 18, fuel: 52 },
    ],
    steer: 1.0, push: 1.15, drift: 0.9, paceScale: 0.95, look: 2.6, wall: 0.86, wallCap: 235, tagA: "TRACTION", tagB: "DRIFT",
  },
  {
    name: "BIKE",
    machines: [
      { name: "A", vmax: 305, jmax: 345, accel: 14, jetAccel: 22, fuel: 45 },
      { name: "B", vmax: 312, jmax: 351, accel: 17.5, jetAccel: 22, fuel: 40 },
    ],
    steer: 1.35, push: 1.25, paceScale: 1.04, tagA: "BURST", tagB: "SPEED",
  },
  {
    name: "KART",
    machines: [
      { name: "A", vmax: 212, jmax: 245, accel: 13, jetAccel: 17, fuel: 45 },
      { name: "B", vmax: 216, jmax: 251, accel: 14.5, jetAccel: 17, fuel: 40 },
    ],
    steer: 1.2, push: 0.7, paceScale: 0.74, look: 2, tagA: "BOOST", tagB: "SPEED",
  },
];
export const vclassOf = (i: number | undefined): VClass => VCLASSES[i ?? 0] ?? VCLASSES[0];
/**
 * Physics revision. Recorded runs, ghosts and challenges carry the revision they were driven with (none = 0) and are replayed with it, so a balance change never alters an old record.
 * 3: a CPU car that a human car is about to lap (a lap or more behind him, close in front of him) moves out of the way, to the side with more room, instead of holding its lane.
 * 2: a car one lap ahead or behind (a lapped car) sits at the same spot of the track as the others: it is drawn on screen and in the mirror, it slipstreams and it can be touched. Before, such a car was invisible and went through you.
 * 1 (1.99.6): formula car B's jet top speed 350 -> 358 (the engine moves in steps of 16 km/h: 350 fell one step below car A's 360, and 352 sits exactly on the step so any slip drops it back).
 */
export const PHYS_REV = 3;
/** The revision stored with a run (0 to 3): anything unknown is the original physics. */
export const physOf = (p: unknown): number => (p === 3 ? 3 : p === 2 ? 2 : p === 1 ? 1 : 0);
const MACHINE_B_REV0: Machine = { ...MACHINES[1], jmax: 350 };
export const machineOf = (car: { machine: number; cls?: number; phys?: number }): Machine =>
  (car.phys ?? 0) < 1 && !car.cls && car.machine === 1 ? MACHINE_B_REV0 : vclassOf(car.cls).machines[car.machine] ?? MACHINES[0];

export const COAST_DECEL = 5; // km/h per tick without throttle (and decay above vmax)
export const BRAKE_DECEL = 10; // km/h per tick while braking
export const STEER_RATE = 1 / 30; // lateral units per tick per km/h
export const JET_BURN_PER_FRAME = 0.035; // litres per video frame (~2.1 L/s)
export const WALL_SPEED_FACTOR = 0.73;
export const WALL_SPEED_CAP = 200;

/** Lateral push of a curve (units per tick) as a function of speed, measured by speed-poking. */
const PUSH: [number, number][] = [
  [0, 0],
  [110, 0],
  [120, 1],
  [210, 1],
  [220, 2],
  [230, 3],
  [240, 3],
  [250, 4],
  [260, 8],
  [270, 9],
  [290, 9],
  [300, 10],
  [310, 10],
  [320, 12],
  [330, 14],
  [340, 16],
  [350, 18],
  [360, 19],
  [500, 19],
];
export function curvePush(speed: number): number {
  if (speed <= 0) return 0;
  for (let i = 1; i < PUSH.length; i++) {
    const [v1, p1] = PUSH[i];
    if (speed <= v1) {
      const [v0, p0] = PUSH[i - 1];
      return p0 + ((p1 - p0) * (speed - v0)) / (v1 - v0);
    }
  }
  return 19;
}

export interface Input {
  accel: boolean;
  brake: boolean;
  jet: boolean;
  left: boolean;
  right: boolean;
}
export const NO_INPUT: Input = { accel: false, brake: false, jet: false, left: false, right: false };

export interface Car {
  id: number;
  machine: number;
  cls: number; // vehicle category (VCLASSES index)
  phys?: number; // physics revision (see PHYS_REV); absent = 0
  pos: number; // absolute distance in units since the start line
  speed: number; // km/h
  lat: number; // lateral position, 128 = road centre
  fuel: number; // litres of jet fuel left
  jetOn: boolean;
  inWall: boolean;
  wallFlash: number; // frames left of the "crash" animation
  bump: number; // frames of side-bump animation
  finished: boolean;
  finishFrame: number;
  lapFrames: number[]; // duration (frames) of each completed lap
  lapStartFrame: number;
  lastLapIdx: number;
  isAI: boolean;
  /** AI: preferred lane and pace in km/h */
  aiLane: number;
  aiPace: number;
  slip: number; // current slipstream bonus (km/h)
  steer: -1 | 0 | 1; // steering input applied this frame (for the lean sprite)
  yaw: number; // displayed body angle: -2..2 (±1 ≈ 10°, ±2 ≈ 20°), sign = turn direction
  curveFrames: number; // frames spent in the current curve
  yawHold: number;
  yawFade: number; // display only: 0..1, how far the pose is into its change to the next, lower one (the renderer dithers the two together)
  slide: number; // lateral speed (units per tick) of a drifting class
}

export function newCar(id: number, machine: number, pos = 0, lat = LAT_START, isAI = false, cls = 0, phys = 0): Car {
  return {
    id,
    ...(phys ? { phys } : {}),
    machine,
    cls,
    pos,
    speed: 0,
    lat,
    fuel: machineOf({ machine, cls }).fuel,
    jetOn: false,
    inWall: false,
    wallFlash: 0,
    bump: 0,
    finished: false,
    finishFrame: 0,
    lapFrames: [],
    lapStartFrame: 0,
    lastLapIdx: 0,
    isAI,
    aiLane: lat,
    aiPace: 270,
    slip: 0,
    steer: 0,
    yaw: 0,
    curveFrames: 0,
    yawHold: 0,
    yawFade: 0,
    slide: 0,
  };
}

export const YAW_DRIFT_SLIDE = 1.6; // rally: lateral speed from which the body shows the curve (a real slide)
export const YAW_RELAX_FRAMES = 10;
export const YAW_FADE_FRAMES = 3; // the last frames before a pose changes blend it into the next one
export const YAW_LONG_CURVE_FRAMES = 36; // a curve held this long switches from the 10° to the 20° pose

/** Pure display state: ±1 for a brief/short turn, ±2 once the curve is sustained. Eases back down slowly. */
function updateYaw(car: Car, track: TrackDef) {
  // the formula and rally cars' pose follows the curve; the bike and kart (human driver only) lean with the steering input
  const kc = curvatureAtPos(track, car.pos);
  let k: number;
  if (car.cls === 1 && !car.isAI) {
    // rally: the body shows the curve once the car really slides (even while the driver counter-steers), otherwise it only leans into the steering; counter-steering in a curve never flips the pose
    k = kc !== 0 && Math.abs(car.slide) > YAW_DRIFT_SLIDE ? kc : kc !== 0 ? (car.steer === kc ? kc : 0) : car.steer;
  } else k = car.cls > 1 && !car.isAI ? car.steer : kc; // formula: the body follows the curve; bike and kart (human driver) lean with the steering input
  car.curveFrames = k !== 0 ? car.curveFrames + 1 : 0;
  let target = 0;
  if (k !== 0 && car.speed > 60) target = k * (car.curveFrames > YAW_LONG_CURVE_FRAMES ? 2 : 1);
  else if (car.steer !== 0 && car.speed > 60 && !(car.cls === 1 && !car.isAI && kc !== 0)) target = car.steer;
  if (target !== 0 && (Math.abs(target) > Math.abs(car.yaw) || Math.sign(target) !== Math.sign(car.yaw))) {
    car.yaw = target; // snap towards a stronger pose
    car.yawHold = 0;
    car.yawFade = 0;
  } else if (target !== car.yaw) {
    if (++car.yawHold > YAW_RELAX_FRAMES) {
      car.yaw -= Math.sign(car.yaw); // relax one level at a time
      car.yawHold = 0;
      car.yawFade = 0;
    } else car.yawFade = Math.max(0, (car.yawHold - (YAW_RELAX_FRAMES - YAW_FADE_FRAMES)) / YAW_FADE_FRAMES);
  } else car.yawFade = 0;
}

/** Advance one car by one video frame. */
export function stepCar(car: Car, input: Input, track: TrackDef, frame: number, slipBonus = 0): void {
  const vc = vclassOf(car.cls);
  const m = machineOf(car);
  const dt = FRAME_PER_TICK; // fraction of a tick elapsed this frame
  let v = car.speed;
  car.jetOn = false;
  car.steer = 0;
  car.slip = slipBonus;

  if (car.wallFlash > 0) car.wallFlash--;
  if (car.bump > 0) car.bump--;

  const sk = track.surfaces ? surfaceAtPos(track, car.pos) : null;
  const fx = sk ? SURFACE_FX[sk] : null;
  const inWallZone = Math.abs(car.lat - LAT_CENTER) > WALL_DIST;
  if (inWallZone) {
    if (!car.inWall) {
      v = Math.min(v * (vc.wall ?? WALL_SPEED_FACTOR), vc.wallCap ?? WALL_SPEED_CAP);
      car.wallFlash = 40;
    }
    car.inWall = true;
    car.slide = 0;
    v = Math.max(8, v - COAST_DECEL * dt);
    // the barrier pushes the car back onto the road, slowly; steering towards the road still works (only the push outwards is cut),
    // so the player is never left without control while the car scrapes the wall
    const toRoad = car.lat < LAT_CENTER ? 1 : -1;
    const steerW = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    car.steer = (steerW === toRoad ? steerW : 0) as -1 | 0 | 1;
    car.lat += toRoad * dt;
    if (steerW === toRoad) car.lat += steerW * v * STEER_RATE * vc.steer * dt;
  } else {
    car.inWall = false;
    const cap = (fx ? m.vmax * fx.vmax : m.vmax) + slipBonus;
    if (input.brake) {
      v = Math.max(0, v - BRAKE_DECEL * (fx ? fx.brake : 1) * dt);
    } else if (input.accel) {
      if (input.jet && car.fuel > 0) {
        car.jetOn = true;
        car.fuel = Math.max(0, car.fuel - JET_BURN_PER_FRAME);
        const jcap = fx ? m.jmax * fx.vmax : m.jmax;
        if (v < jcap) v = Math.min(jcap, v + m.jetAccel * (fx ? fx.accel : 1) * dt);
        else v = Math.max(jcap, v - COAST_DECEL * dt);
      } else if (v < cap) {
        v = Math.min(cap, v + m.accel * (fx ? fx.accel : 1) * dt);
      } else {
        v = Math.max(cap, v - COAST_DECEL * dt);
      }
    } else {
      v = Math.max(0, v - COAST_DECEL * dt);
    }

    // steering and centrifugal push, both measured per tick
    const steer = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    car.steer = steer as -1 | 0 | 1;
    const k = curvatureAtPos(track, car.pos);
    const st = fx ? fx.steer : 1, ps = fx ? fx.push : 1;
    if (vc.drift) {
      const want = steer * v * STEER_RATE * vc.steer * st - k * curvePush(v) * vc.push * ps;
      const dr = fx ? 1 - (1 - vc.drift) / fx.slide : vc.drift;
      car.slide = car.slide * dr + want * (1 - dr);
      car.lat += car.slide * dt;
    } else {
      car.lat += steer * v * STEER_RATE * vc.steer * st * dt; // (same order as always: recorded runs depend on it)
      car.lat -= k * curvePush(v) * vc.push * ps * dt;
    }
  }
  car.lat = Math.max(LAT_CENTER - LAT_CLAMP, Math.min(LAT_CENTER + LAT_CLAMP, car.lat));
  car.speed = v;

  updateYaw(car, track);

  const eff = Math.floor(v / SPEED_QUANT) * SPEED_QUANT;
  const before = car.pos;
  car.pos += UNIT_PER_TICK_FACTOR * eff * dt;
  if (track.hillPhysics) {
    // gravity: the speed pays for the height gained over this step and gets back what is lost
    const de = elevationAtPos(track, car.pos) - elevationAtPos(track, before);
    const ceil = Math.max(car.speed, m.jmax + HILL_OVERSPEED); // a long descent gives a boost, not a rocket
    // brakes hold the car: a descent adds no speed while the brake is down (on a steep one the gravity used to cancel the whole braking)
    const gain = input.brake && !inWallZone && de < 0 ? 0 : HILL_GRAVITY * de;
    car.speed = Math.min(ceil, Math.max(0, car.speed - gain));
  }
}

export interface RaceOptions {
  track: TrackDef;
  playerMachine: number;
  withAI: boolean;
  seed?: number;
}

/** Small deterministic PRNG (mulberry32). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** CPU difficulty levels. PRO (1) is the original balance and the default everywhere (online, tests). */
export interface Skill {
  name: string;
  paceBase: number; // km/h
  paceRange: number; // random spread added per car
  curveCap: number; // max pace in and just before curves
  ahead: number; // slow-down applied when the CPU is far ahead of the humans
  behind: number; // speed-up applied when far behind
  gapAhead: number; // distance thresholds (units) for the rubber band
  gapBehind: number;
  jet: 0 | 1 | 2; // 0 never, 1 when chasing a human or on the last lap, 2 on every straight
  fastMachines: number; // 1 in N cars drives the faster type B (0 = none)
}
export const SKILLS: Skill[] = [
  { name: "ROOKIE", paceBase: 236, paceRange: 20, curveCap: 244, ahead: 24, behind: 2, gapAhead: 9000, gapBehind: 30000, jet: 0, fastMachines: 0 },
  { name: "PRO", paceBase: 262, paceRange: 26, curveCap: 268, ahead: 14, behind: 10, gapAhead: 18000, gapBehind: 18000, jet: 0, fastMachines: 3 },
  { name: "EXPERT", paceBase: 276, paceRange: 18, curveCap: 276, ahead: 5, behind: 14, gapAhead: 26000, gapBehind: 14000, jet: 1, fastMachines: 2 },
  { name: "CHAMPION", paceBase: 288, paceRange: 14, curveCap: 284, ahead: 0, behind: 18, gapAhead: 40000, gapBehind: 10000, jet: 2, fastMachines: 2 },
];

export type Phase = "countdown" | "racing" | "finished";

export const COUNTDOWN_FRAMES = 60 * 3 + 40;

export class Race {
  track: TrackDef;
  cars: Car[] = [];
  frame = 0; // frames since the lights sequence began
  phase: Phase = "countdown";
  goFrame = COUNTDOWN_FRAMES;
  readonly rand: () => number;
  skill = 1;

  readonly vclass: number;
  /** Physics revision 2: a car one lap ahead or behind sits at the same spot of the track, so it is seen and touched like any other. */
  readonly lapWrap: boolean;
  /** Physics revision 3: lapped CPU cars let the leader through. */
  readonly yieldLapped: boolean;
  constructor(opts: { track: TrackDef; machines: number[]; aiCount: number; seed: number; lat?: number[]; skill?: number; vclass?: number; phys?: number }) {
    this.lapWrap = physOf(opts.phys) >= 2;
    this.yieldLapped = physOf(opts.phys) >= 3;
    this.vclass = opts.vclass !== undefined && VCLASSES[opts.vclass] ? opts.vclass : 0;
    this.skill = Math.max(0, Math.min(SKILLS.length - 1, opts.skill ?? 1));
    const sk = SKILLS[this.skill];
    this.track = opts.track;
    this.rand = rng(opts.seed);
    // Player slots (client index 0..n-1) start at the back of the grid, staggered
    const n = opts.machines.length;
    for (let i = 0; i < n; i++) {
      const lat = opts.lat?.[i] ?? (i % 2 === 0 ? 144 : 112);
      this.cars.push(newCar(i, opts.machines[i], -Math.floor(i / 2) * 2400, lat, false, this.vclass, opts.phys));
    }
    // AI cars on the grid in front
    for (let k = 0; k < opts.aiCount; k++) {
      const row = Math.floor(k / 2) + 1;
      const col = k % 2;
      const fast = sk.fastMachines > 0 && k % sk.fastMachines === sk.fastMachines - 1;
      const car = newCar(n + k, fast ? 1 : 0, row * 2600, col ? 144 : 112, true, this.vclass, opts.phys);
      car.aiLane = col ? 140 : 116;
      car.aiPace = (sk.paceBase + this.rand() * sk.paceRange) * vclassOf(this.vclass).paceScale;
      this.cars.push(car);
    }
  }

  get raceFrame(): number {
    return this.frame - this.goFrame;
  }

  /** inputs: indexed by car id for human cars; AI inputs are generated. */
  step(inputs: Input[]): void {
    this.frame++;
    if (this.phase === "countdown") {
      if (this.frame >= this.goFrame) this.phase = "racing";
      return;
    }
    const track = this.track;
    const total = track.lapUnits * track.laps;
    const rf = this.raceFrame;
    const lead = this.cars.reduce((a, c) => Math.max(a, c.pos), 0);
    for (const car of this.cars) {
      if (car.finished) {
        // roll out after the flag
        stepCar(car, { ...NO_INPUT }, track, rf);
        continue;
      }
      let input = inputs[car.id] ?? NO_INPUT;
      if (car.isAI) input = this.aiInput(car, lead);
      const slip = this.slipstream(car);
      stepCar(car, input, track, rf, slip);
      // laps
      const lapIdx = Math.floor(car.pos / track.lapUnits);
      if (lapIdx > car.lastLapIdx && car.pos >= 0) {
        car.lapFrames.push(rf - car.lapStartFrame);
        car.lapStartFrame = rf;
        car.lastLapIdx = lapIdx;
      }
      if (car.pos >= total) {
        car.finished = true;
        car.finishFrame = rf;
      }
    }
    this.collide();
    this.collide(); // second pass settles chains of 3+ cars
    if (this.cars.filter((c) => !c.isAI).every((c) => c.finished)) this.phase = "finished";
  }

  /** Distance along the track from `from` to `to`; with lapWrap a car a lap (or more) away counts as being next to it. */
  relPos(from: { pos: number }, to: { pos: number }): number {
    const dz = to.pos - from.pos;
    if (!this.lapWrap) return dz;
    const L = this.track.lapUnits;
    return ((((dz + L / 2) % L) + L) % L) - L / 2;
  }

  /** Drafting: a car close behind another one gains a speed bonus (seen up to ~+30 km/h). */
  private slipstream(car: Car): number {
    let best = 0;
    for (const o of this.cars) {
      if (o === car) continue;
      const dz = this.relPos(car, o);
      if (dz > 200 && dz < 4200 && Math.abs(o.lat - car.lat) < 14) {
        best = Math.max(best, 30 * (1 - dz / 4200));
      }
    }
    return best;
  }

  /** Car-to-car contact: the rear car loses speed and both are nudged sideways. */
  private collide(): void {
    for (let i = 0; i < this.cars.length; i++) {
      for (let j = i + 1; j < this.cars.length; j++) {
        const a = this.cars[i],
          b = this.cars[j];
        const dz = this.relPos(a, b);
        const dl = b.lat - a.lat;
        if (Math.abs(dz) < 700 && Math.abs(dl) < 20) {
          const rear = dz > 0 ? a : b;
          const front = dz > 0 ? b : a;
          rear.speed = Math.min(rear.speed, Math.max(front.speed - 5, 0) * 0.97);
          // solid bodies: the rear car can never get closer than GAP, so sprites never interpenetrate
          const GAP = 420;
          if (Math.abs(dz) < GAP) {
            if (!this.lapWrap) {
              if (dz > 0) a.pos = b.pos - GAP;
              else b.pos = a.pos - GAP;
            } else if (dz > 0) a.pos += dz - GAP; // (a lap apart: move along the track, not to the other car's number)
            else b.pos += -dz - GAP;
          }
          // side push grows the deeper the lateral overlap, so cars slide apart instead of stacking
          const push = dl === 0 ? (i % 2 ? 1 : -1) : Math.sign(dl);
          const k = 0.5 + (1 - Math.abs(dl) / 20) * 1.0;
          a.lat -= push * k;
          b.lat += push * k;
          a.bump = b.bump = 12;
        }
      }
    }
  }

  /** Simple lane-keeping, pace-keeping AI using the same physics as the player. */
  private aiInput(car: Car, lead: number): Input {
    const track = this.track;
    // rubber band relative to the human cars
    let human = 0,
      cnt = 0;
    for (const c of this.cars) if (!c.isAI) (human += c.pos), cnt++;
    const hp = cnt ? human / cnt : lead;
    const gap = car.pos - hp;
    let pace = car.aiPace;
    const sk = SKILLS[this.skill];
    if (gap > sk.gapAhead) pace -= sk.ahead;
    else if (gap < -sk.gapBehind) pace += sk.behind;
    const m = machineOf(car);
    const ps = vclassOf(this.vclass).paceScale;
    pace = Math.min(pace, m.vmax + 4);
    // slow down for curves ahead a little: grip depends on speed
    const f = ((car.pos / track.lapUnits) % 1 + 1) % 1;
    const inCurve = curvatureAtPos(track, car.pos) !== 0 || curvatureAtPos(track, car.pos + 9000) !== 0;
    if (inCurve) pace = Math.min(pace, (sk.curveCap - (car.id % 3) * 3) * ps);
    void f;
    // a human car about to lap this one (revision 3): leave the racing line to the side with more room
    let lane = car.aiLane;
    if (this.yieldLapped) {
      for (const h of this.cars) {
        if (h.isAI || h.finished) continue;
        const behind = this.relPos(car, h); // < 0: he is behind me on the track
        if (h.pos - car.pos > track.lapUnits * 0.5 && behind > -6000 && behind < 800 && Math.abs(h.lat - car.lat) < 28) {
          lane = car.lat >= h.lat ? 156 : 100;
          break;
        }
      }
    }
    const left = car.lat > lane + 3 + (curvatureAtPos(track, car.pos) > 0 ? 1 : 0) * -6;
    const right = car.lat < lane - 3 + (curvatureAtPos(track, car.pos) > 0 ? 1 : 0) * 6;
    // higher levels burn their jet fuel: on straights only, keeping a little in reserve
    const lastLap = car.pos > track.lapUnits * (track.laps - 1);
    const wantJet =
      sk.jet > 0 && !inCurve && !car.inWall && car.fuel > (sk.jet === 2 ? 4 : 20) && car.speed > 120 &&
      (sk.jet === 2 || gap < 0 || lastLap);
    if (wantJet) return { accel: true, brake: false, jet: true, left: left && !right, right: right && !left };
    return {
      accel: car.speed < pace,
      brake: car.speed > pace + 12,
      jet: false,
      left: left && !right,
      right: right && !left,
    };
  }

  /** 1-based rank of a car by distance covered. */
  rankOf(car: Car): number {
    let r = 1;
    for (const o of this.cars) {
      if (o === car) continue;
      if (o.finished && !car.finished) r++;
      else if (o.finished && car.finished) {
        if (o.finishFrame < car.finishFrame) r++;
      } else if (!o.finished && !car.finished && o.pos > car.pos) r++;
    }
    return r;
  }

  /** Lap currently being driven (1-based), clamped to track.laps. */
  lapOf(car: Car): number {
    return Math.min(this.track.laps, Math.max(0, Math.floor(car.pos / this.track.lapUnits)) + 1);
  }
}

/** Format frames as m:ss:cc like the original HUD. */
export function formatTime(frames: number): string {
  const total = Math.max(0, frames) / 59.7275;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const cs = Math.floor((total * 100) % 100);
  return `${m}:${String(s).padStart(2, "0")}:${String(cs).padStart(2, "0")}`;
}
